/**
 * useScaleWeight — tartıdan canlı ağırlık aboneliği
 *
 * local-service'in `/api/scale/events` SSE akışına bağlanır. Bağlantı kurulduğu
 * anda local-service tartıyı yoklamaya başlar, son dinleyici ayrılınca durur;
 * bu yüzden `enabled` yalnızca ağırlığa gerçekten ihtiyaç duyulan ekran/modal
 * açıkken true verilmelidir. Aksi halde kasa boştayken tartıya sürekli trafik
 * gider.
 *
 * SSE kalıbı usePhoneCalls.ts'teki Caller ID bağlantısıyla aynı: üstel geri
 * çekilmeli yeniden bağlanma ve dört durumlu bağlantı göstergesi.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ScaleReading, ScaleTarget, scaleEventsUrl } from '../services/localScaleService';

export type ScaleConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

export interface UseScaleWeightOptions {
  /** Yalnızca ağırlık gerektiğinde true verin — yoklamayı bu tetikliyor. */
  enabled: boolean;
  /** Hangi tartı. Null ise (ekranda tartı seçili değil) bağlantı hiç açılmaz. */
  target: ScaleTarget | null;
  localServiceUrl?: string | null;
}

export interface UseScaleWeightResult {
  reading: ScaleReading | null;
  /** Ağırlık oturduğunda true (ardışık eşit okuma ile belirlenir). */
  isStable: boolean;
  status: ScaleConnectionStatus;
  error: string | null;
  /** Ekranda bir tartı seçili mi. */
  isConfigured: boolean;
}

const MAX_RECONNECT_ATTEMPTS = 50;
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

export function useScaleWeight({ enabled, target, localServiceUrl }: UseScaleWeightOptions): UseScaleWeightResult {
  const [reading, setReading] = useState<ScaleReading | null>(null);
  const [status, setStatus] = useState<ScaleConnectionStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cleanedUpRef = useRef(false);

  const closeConnection = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  // Nesne kimliği her render'da değişmesin diye alanlarına bağlanıyoruz;
  // aksi halde effect her render'da SSE bağlantısını yeniden kurardı.
  const host = target?.host || '';
  const port = target?.port;
  const model = target?.model;

  useEffect(() => {
    if (!enabled || !host) {
      closeConnection();
      setStatus('idle');
      setReading(null);
      setError(null);
      return;
    }

    cleanedUpRef.current = false;
    reconnectAttemptRef.current = 0;
    const url = scaleEventsUrl({ host, port, model }, localServiceUrl);

    const connect = () => {
      if (cleanedUpRef.current) return;

      setStatus('connecting');
      try {
        if (eventSourceRef.current) eventSourceRef.current.close();

        const source = new EventSource(url);
        eventSourceRef.current = source;

        source.onopen = () => {
          if (cleanedUpRef.current) return;
          setStatus('connected');
          reconnectAttemptRef.current = 0;
        };

        source.addEventListener('connected', () => {
          setStatus('connected');
        });

        source.addEventListener('weight', (event) => {
          try {
            const payload = JSON.parse((event as MessageEvent).data);
            const data = (payload?.data || payload) as ScaleReading;
            setReading(data);
            setError(null);
            setStatus('connected');
          } catch (_) { /* tek bozuk olay akışı bozmasın */ }
        });

        source.addEventListener('error', (event) => {
          // Tartı kaynaklı hata (bağlantı kopması değil): local-service
          // okuma başarısız olduğunda bu olayı yayınlıyor.
          try {
            const payload = JSON.parse((event as MessageEvent).data);
            setError(payload?.data?.error || 'Tartı okunamadı');
          } catch (_) { /* SSE'nin kendi error olayı: onerror ele alıyor */ }
        });

        source.onerror = () => {
          if (cleanedUpRef.current) return;
          setStatus('error');
          source.close();
          eventSourceRef.current = null;

          if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
            setStatus('disconnected');
            return;
          }
          reconnectAttemptRef.current++;
          const delay = Math.min(
            INITIAL_RECONNECT_DELAY_MS * Math.pow(1.5, reconnectAttemptRef.current - 1),
            MAX_RECONNECT_DELAY_MS
          );
          reconnectTimerRef.current = setTimeout(connect, delay);
        };
      } catch (err) {
        setStatus('error');
      }
    };

    connect();

    return () => {
      cleanedUpRef.current = true;
      closeConnection();
    };
  }, [enabled, host, port, model, localServiceUrl, closeConnection]);

  return {
    reading,
    isStable: !!reading?.stable,
    status,
    error,
    isConfigured: !!host,
  };
}

export default useScaleWeight;
